import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Plus, Check, Trash2, Star, Building2, Shield,
  DollarSign, Clock, AlertCircle, Wallet, Smartphone, Loader2,
  ChevronRight, ExternalLink, CheckCircle2, XCircle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Platform Icons (SVG-based for better rendering)
const PlatformIcon = ({ platform, size = 24 }) => {
  const icons = {
    venmo: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#008CFF">
        <path d="M19.26 2c.78 1.29 1.13 2.62 1.13 4.3 0 5.36-4.57 12.32-8.29 17.2H5.52L2.74 3.51l5.86-.54 1.49 11.99c1.39-2.27 3.1-5.83 3.1-8.27 0-1.57-.27-2.65-.71-3.53L19.26 2z"/>
      </svg>
    ),
    chime: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#00D54B">
        <circle cx="12" cy="12" r="10" stroke="#00D54B" strokeWidth="2" fill="none"/>
        <path d="M12 6v6l4 2" stroke="#00D54B" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    cashapp: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#00D632">
        <rect x="2" y="2" width="20" height="20" rx="4" fill="#00D632"/>
        <path d="M12 6v12M8 10l4-4 4 4M8 14l4 4 4-4" stroke="white" strokeWidth="2" fill="none"/>
      </svg>
    ),
    zelle: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#6D1ED4">
        <circle cx="12" cy="12" r="11" fill="#6D1ED4"/>
        <path d="M7 8h10l-7 8h10" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    netspend: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#FF6B00">
        <rect x="2" y="5" width="20" height="14" rx="2" fill="#FF6B00"/>
        <rect x="2" y="9" width="20" height="3" fill="#CC5500"/>
        <circle cx="18" cy="15" r="2" fill="#FFD700"/>
      </svg>
    )
  };
  
  return icons[platform] || <Wallet size={size} />;
};

// Single Deposit Account Card
const DepositAccountCard = ({ account, onSetPrimary, onRemove, isLoading }) => {
  return (
    <div 
      className={`relative p-4 rounded-xl border-2 transition-all ${
        account.is_primary 
          ? 'border-green-500 bg-green-50' 
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      data-testid={`deposit-account-${account.account_id}`}
    >
      {/* Primary Badge */}
      {account.is_primary && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <Star className="w-3 h-3" />
          Primary
        </div>
      )}
      
      <div className="flex items-start gap-3">
        {/* Platform Icon */}
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${account.color}20` }}
        >
          <PlatformIcon platform={account.platform_id} size={28} />
        </div>
        
        {/* Account Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{account.platform_name}</span>
            {account.verification_status === 'verified' && (
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            )}
          </div>
          <p className="text-sm text-gray-600 font-mono">{account.display_identifier}</p>
          {account.nickname && account.nickname !== account.platform_name && (
            <p className="text-xs text-gray-400 mt-0.5">{account.nickname}</p>
          )}
          
          {/* Stats */}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              {account.total_payouts || 0} payouts
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {account.processing_time}
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-1">
          {!account.is_primary && (
            <button
              onClick={() => onSetPrimary(account.account_id)}
              disabled={isLoading}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Set as Primary"
            >
              <Star className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onRemove(account.account_id, account.platform_name)}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove Account"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Add New Account Form
const AddAccountForm = ({ platforms, onAdd, onCancel, isLoading }) => {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [nickname, setNickname] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedPlatform || !identifier) {
      toast.error('Please fill all required fields');
      return;
    }
    onAdd({
      platform_id: selectedPlatform.platform_id,
      account_identifier: identifier,
      nickname,
      is_primary: isPrimary
    });
  };
  
  return (
    <div className="bg-white rounded-2xl border-2 border-blue-200 p-6" data-testid="add-deposit-form">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Plus className="w-5 h-5 text-blue-600" />
        Add Payout Method
      </h3>
      
      {/* Platform Selection */}
      {!selectedPlatform ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {platforms.map((platform) => (
            <button
              key={platform.platform_id}
              onClick={() => setSelectedPlatform(platform)}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
              style={{ '--hover-color': platform.color }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${platform.color}20` }}
              >
                <PlatformIcon platform={platform.platform_id} size={28} />
              </div>
              <span className="text-sm font-medium text-gray-700">{platform.name}</span>
              {platform.verified_institution && (
                <Shield className="w-4 h-4 text-green-500" />
              )}
            </button>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Selected Platform Header */}
          <div 
            className="flex items-center gap-3 p-3 rounded-xl"
            style={{ backgroundColor: `${selectedPlatform.color}10` }}
          >
            <PlatformIcon platform={selectedPlatform.platform_id} size={32} />
            <div>
              <p className="font-semibold text-gray-900">{selectedPlatform.name}</p>
              <p className="text-xs text-gray-500">{selectedPlatform.parent_company}</p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedPlatform(null)}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            >
              Change
            </button>
          </div>
          
          {/* Account Identifier Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedPlatform.field_label} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={selectedPlatform.placeholder}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-colors"
              required
            />
            {selectedPlatform.bank_partners && selectedPlatform.bank_partners.length > 0 && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Supported: {selectedPlatform.bank_partners.slice(0, 4).join(', ')}...
              </p>
            )}
          </div>
          
          {/* Nickname (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nickname (Optional)
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g., My Primary Account"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-colors"
            />
          </div>
          
          {/* Set as Primary */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-700">Set as primary payout method</span>
          </label>
          
          {/* Platform Info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Processing Time:</span>
              <span className="font-medium text-gray-900">{selectedPlatform.processing_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Fees:</span>
              <span className="font-medium text-green-600">
                {selectedPlatform.fees === 0 ? 'Free' : `$${selectedPlatform.fees}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Payout Range:</span>
              <span className="font-medium text-gray-900">
                ${selectedPlatform.min_payout} - ${selectedPlatform.max_payout.toLocaleString()}
              </span>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !identifier}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add Account
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Main Component
export default function ProviderDepositSettings() {
  const [accounts, setAccounts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('accounts');
  
  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, platformsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/provider-deposits/accounts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/provider-deposits/platforms`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/provider-deposits/payout-history`, { headers: getAuthHeaders() })
      ]);
      
      setAccounts(accountsRes.data.accounts || []);
      setPlatforms(platformsRes.data.platforms || []);
      setPayoutHistory(historyRes.data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch deposit data:', error);
      toast.error('Failed to load payout settings');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleAddAccount = async (data) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/provider-deposits/add`, data, { headers: getAuthHeaders() });
      toast.success(`${data.platform_id.charAt(0).toUpperCase() + data.platform_id.slice(1)} account added!`);
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add account');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleSetPrimary = async (accountId) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/api/provider-deposits/accounts/${accountId}/primary`, {}, { headers: getAuthHeaders() });
      toast.success('Primary account updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update primary account');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRemoveAccount = async (accountId, platformName) => {
    if (!window.confirm(`Remove ${platformName} account? This cannot be undone.`)) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/api/provider-deposits/accounts/${accountId}`, { headers: getAuthHeaders() });
      toast.success(`${platformName} account removed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove account');
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="provider-deposit-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payout Methods</h2>
          <p className="text-gray-500">Manage how you receive your earnings</p>
        </div>
        {!showAddForm && accounts.length < 5 && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Method
          </button>
        )}
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('accounts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'accounts'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Wallet className="w-4 h-4 inline mr-2" />
          Accounts
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-2" />
          Payout History
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'accounts' && (
        <div className="space-y-4">
          {/* Add Form */}
          {showAddForm && (
            <AddAccountForm
              platforms={platforms}
              onAdd={handleAddAccount}
              onCancel={() => setShowAddForm(false)}
              isLoading={actionLoading}
            />
          )}
          
          {/* Existing Accounts */}
          {accounts.length > 0 ? (
            <div className="space-y-3">
              {accounts.map((account) => (
                <DepositAccountCard
                  key={account.account_id}
                  account={account}
                  onSetPrimary={handleSetPrimary}
                  onRemove={handleRemoveAccount}
                  isLoading={actionLoading}
                />
              ))}
            </div>
          ) : !showAddForm ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
              <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payout Methods</h3>
              <p className="text-gray-500 mb-4">Add a payout method to receive your earnings</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Your First Method
              </button>
            </div>
          ) : null}
          
          {/* Trusted Partners Banner */}
          {!showAddForm && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 flex items-center gap-4">
              <Shield className="w-10 h-10 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">Trusted Financial Partners</p>
                <p className="text-sm text-gray-600">
                  All platforms are verified and backed by major financial institutions
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'history' && (
        <div className="space-y-4">
          {payoutHistory.length > 0 ? (
            <div className="space-y-2">
              {payoutHistory.map((payout) => (
                <div
                  key={payout.payout_id}
                  className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payout.status === 'completed' ? 'bg-green-100' : 
                        payout.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                      }`}
                    >
                      {payout.status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : payout.status === 'pending' ? (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {payout.platform_name} - {payout.display_identifier}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(payout.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">+${payout.amount.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 capitalize">{payout.status}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No payout history yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
